// 用户登录态 / 信息全局 store
//
// Pinia 的好处：组件里 const userStore = useUserStore() 直接读 userStore.token
// 任何地方调 userStore.logout() 都会同步路由跳转 + 清 localStorage。

import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  fetchSelfByToken,
  loginByAccount,
  loginByMobile,
  loginByThirdParty,
  normalizeUser,
  registerGuest,
  sendSmsCode,
  logout as apiLogout,
  type ThirdPartyLoginParams,
  type UserInfo,
} from "@/api/user";
import {
  canAccessGame,
  getVipStatus,
  isPremium as isPremiumVip,
  isSvip as isSvipVip,
  isVipExpiringSoon,
  parseVipLevel,
  vipDaysLeft,
  type VipStatus,
} from "@/api/account";
import {
  clearGuestCredentials,
  getGuestCredentials,
  getIsGuest,
  getToken,
  setGuestCredentials,
  setIsGuest,
  setToken,
} from "@/api/client";
import { regenerateDeviceId } from "@/utils/device";

export const useUserStore = defineStore("user", () => {
  const token = ref<string>(getToken());
  const userInfo = ref<UserInfo | null>(null);
  const loading = ref(false);
  const isGuest = ref<boolean>(getIsGuest());

  const isLoggedIn = computed(() => !!token.value);
  // VIP 状态机：none / vip / svip / vip_expired / svip_expired
  const vipStatus = computed<VipStatus>(() => getVipStatus(userInfo.value));
  // vipLevel 不考虑过期，给"曾经是 VIP"的展示用
  const vipLevel = computed(() => parseVipLevel(userInfo.value));
  // isPremium / isSvip 都考虑过期，做权限闸门
  const isPremium = computed(
    () => isLoggedIn.value && isPremiumVip(userInfo.value),
  );
  const isSvip = computed(
    () => isLoggedIn.value && isSvipVip(userInfo.value),
  );
  const vipDaysRemaining = computed(() => vipDaysLeft(userInfo.value));
  const vipExpiringSoon = computed(
    () => isLoggedIn.value && isVipExpiringSoon(userInfo.value, 7),
  );

  /** 检查用户对某个游戏的访问权限（按 corner_mark） */
  function canPlayGame(cornerMark: unknown): boolean {
    return canAccessGame(userInfo.value, cornerMark);
  }

  // 监听 api/client.ts 里 setToken 的事件，让 ref 永远跟 localStorage 一致
  // 防止 interceptor 401 清 token 后这里还显示已登录
  if (typeof window !== "undefined") {
    window.addEventListener("jzbro:token-changed", ((e: Event) => {
      const newToken =
        (e as CustomEvent<{ token: string }>).detail?.token ?? "";
      if (token.value !== newToken) {
        token.value = newToken;
        if (!newToken) {
          userInfo.value = null;
        }
      }
    }) as EventListener);
  }

  /** 把 accountlogin / regist 返回的字段映射成 UI 一直在用的 user_xxx 命名 */
  function normalizeAccountUser(
    raw: import("@/api/user").AccountLoginResult,
  ): UserInfo {
    return normalizeUser(raw) ?? ({} as UserInfo);
  }

  async function bootstrap(silent = true): Promise<void> {
    if (!token.value) {
      // 没 token 但有游客凭证 → 自动用凭证重新登录（设备升级 / 清 cookie 都能恢复）
      if (isGuest.value) {
        const creds = getGuestCredentials();
        if (creds) {
          await silentGuestRelogin(creds.username, creds.password);
        }
      }
      return;
    }
    loading.value = true;
    try {
      const info = await fetchSelfByToken(silent);
      if (info) {
        userInfo.value = info;
      } else {
        // token 失效。如果是游客，凭证还在，再走 accountlogin 救回
        setToken("");
        token.value = "";
        if (isGuest.value) {
          const creds = getGuestCredentials();
          if (creds) {
            await silentGuestRelogin(creds.username, creds.password);
          }
        }
      }
    } finally {
      loading.value = false;
    }
  }

  /** 按需获取 userInfo：如果已登录但 userInfo 没值（bootstrap 失败 / 网络抖了），
   *  组件 onMounted 可以调这个补救。idempotent，已有值不重复请求。 */
  async function ensureUserInfo(force = false): Promise<void> {
    if (!isLoggedIn.value) return;
    if (userInfo.value && !force) return;
    await bootstrap();
  }

  async function silentGuestRelogin(
    username: string,
    password: string,
  ): Promise<void> {
    try {
      const data = await loginByAccount(username, password, true);
      token.value = data.token;
      userInfo.value = normalizeAccountUser(data);
      setIsGuest(true);
      isGuest.value = true;
    } catch {
      /* 自动恢复静默失败。 */
    }
  }

  async function requestSmsCode(mobile: string): Promise<void> {
    await sendSmsCode(mobile);
  }

  async function loginWithSms(mobile: string, yzm: string): Promise<void> {
    const data = await loginByMobile(mobile, yzm);
    token.value = data.token;
    // mobilelogin 返回扁平结构（token + id/nickname/avatar/vip_type…），跟 accountlogin
    // 一样, 用 normalizeAccountUser 统一成 UserInfo（之前直接读 data.user_info 永远 undefined）
    userInfo.value = normalizeAccountUser(data);
    // 手机号登录 = 实名/正式账号, 清掉游客标记（这样"我的"页不再显示游客徽章）
    setIsGuest(false);
    isGuest.value = false;
  }

  /** 第三方登录（海外 Google / Facebook 等），成功后写入会话。 */
  async function loginWithThirdParty(
    params: ThirdPartyLoginParams,
  ): Promise<void> {
    const data = await loginByThirdParty(params);
    token.value = data.token;
    userInfo.value = normalizeAccountUser(data);
    setIsGuest(false);
    isGuest.value = false;
  }

  function logout(): void {
    apiLogout();
    token.value = "";
    userInfo.value = null;
    // 注意：不清 guest creds、不动 isGuest 标记
    // 这样游客点"退出"也能保留身份，下次进站自动复登
  }

  // ============ 游客模式 ============

  /** 游客登录：本地有凭证就直接 accountlogin；没有就 regist/fast 拿凭证再 login
   *  失效兜底：accountlogin 报"用户不存在"(101008) 就清掉旧凭证重新注册 */
  async function loginAsGuest(): Promise<void> {
    loading.value = true;
    try {
      let creds = getGuestCredentials();
      let data = null as Awaited<ReturnType<typeof loginByAccount>> | null;

      if (creds) {
        try {
          data = await loginByAccount(creds.username, creds.password);
        } catch (e) {
          // 接口 envelope code 101008 = 用户不存在 → 凭证失效，清掉重新注册
          const code = (e as { code?: number })?.code;
          if (code === 101008) {
            clearGuestCredentials();
            creds = null;
          } else {
            throw e;
          }
        }
      }

      if (!data) {
        // 没凭证或凭证失效 → 重新走 regist/fast
        const reg = await registerGuest();
        creds = { username: reg.username, password: reg.password };
        setGuestCredentials(creds);
        data = await loginByAccount(creds.username, creds.password);
      }

      token.value = data.token;
      userInfo.value = normalizeAccountUser(data);
      setIsGuest(true);
      isGuest.value = true;
    } finally {
      loading.value = false;
    }
  }

  /** 重置游客身份：清凭证 + 换 Device-Id，下次 loginAsGuest 会创建全新账号 */
  async function resetGuestIdentity(): Promise<void> {
    clearGuestCredentials();
    regenerateDeviceId();
    token.value = "";
    userInfo.value = null;
    isGuest.value = false;
    setToken("");
  }

  // 给外部直接塞 token 用（比如三方登录 / 一键登录回调）
  function setSession(tk: string, info: UserInfo) {
    setToken(tk);
    token.value = tk;
    userInfo.value = info;
  }

  return {
    token,
    userInfo,
    loading,
    isLoggedIn,
    isGuest,
    isPremium,
    isSvip,
    vipLevel,
    vipStatus,
    vipDaysRemaining,
    vipExpiringSoon,
    canPlayGame,
    bootstrap,
    ensureUserInfo,
    requestSmsCode,
    loginWithSms,
    loginWithThirdParty,
    loginAsGuest,
    resetGuestIdentity,
    logout,
    setSession,
  };
});
